import { distinctUntilChanged, map, takeWhile } from 'rxjs'

import { PathTool } from '../path/Path.tool'
import { DRAWING_TYPES, PAPER_ELEMENT_TYPE } from '../../../../../../shared/constants/drawable-types'
import { applyScaleFactorToPathArea } from '../../../../../../utils/calculations/scaleConversion/scaleConversion'
import { initialToolsState, ToolsState } from '../../../../../slices/tools'
import {
    AnalyticsActionEdgeType,
    AnalyticsActionType,
    Cursors,
    IMUPState,
    PaperToolConfig,
    VIEW_MODE,
} from '../../../../../types'

/**
 * Cutout.tool.tsx
 * Creates a corner to corner Cutout upon mouse drag
 */
export class Cutout extends PathTool {
    static NAME = 'CUTOUT'
    static CURSOR = Cursors.CROSSHAIR

    private startingPoint: paper.Point | null = null
    private cutoutPath: paper.Path | null = null

    private color: ToolsState['color'] = initialToolsState.color
    private opacity: ToolsState['areaOpacityValue'] = initialToolsState.areaOpacityValue
    private currentPaperId: number | null = null
    private rectangleId: number | null = null
    private cutoutItemId: number | null = null
    private activeGroupId: number | null = null
    private itemMetaDataUpdateFunction: ((item: paper.Item) => void) | null = null
    private cachedParentItem: paper.Item | null = null

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Cutout.NAME
        this.cursor = Cutout.CURSOR

        this.mediator
            .get$()
            .pipe(
                takeWhile(({ common: { activeMode } }: IMUPState) => activeMode === VIEW_MODE.Markup2D),
                map(({ tools: { areaOpacityValue, cutoutItem }, geometry }: IMUPState) => ({
                    areaOpacityValue,
                    cutoutItem,
                    activeGroupId: geometry.activeGeometryGroupID,
                })),
                distinctUntilChanged(
                    (prev, curr) =>
                        prev.areaOpacityValue === curr.areaOpacityValue &&
                        prev.cutoutItem === curr.cutoutItem &&
                        prev.activeGroupId === curr.activeGroupId
                )
            )
            .subscribe(({ areaOpacityValue, cutoutItem, activeGroupId }) => {
                this.opacity = areaOpacityValue
                this.cutoutItemId = cutoutItem
                this.activeGroupId = activeGroupId
            })
    }

    protected draw(color: paper.Color | null, from: paper.Point, to: paper.Point): paper.Path.Rectangle {
        const path = new this.paper.Path.Rectangle(from, to)

        path.strokeColor = color
        path.opacity = this.opacity
        path.fillColor = color
        path.data.shapeType = DRAWING_TYPES.AREA
        path.strokeWidth = 0

        this.currentPaperId = path.id

        return path
    }

    /**
     * Clear the points that are being drawn with the tool
     */
    cancel = () => {
        const rect = this.paper.project.getItem({ id: this.currentPaperId })

        if (rect) rect.remove()
        this.cutoutPath = null
        this.rectangleId = null
        this.startingPoint = null
        this.cachedParentItem = null

        this.setState('common', { cursor: this.cursor, tooltip: { title: '', visible: false, color: '#000000' } })
    }

    /**
     * Merges overlapping or adjacent cutouts into a single cutout path
     * @param cutouts Array of cutout paths (excluding the first child which is the parent)
     * @param newCutout The new cutout to merge
     * @returns Array of merged cutout paths
     */
    private mergeCutouts = (cutouts: paper.Item[], newCutout: paper.Path): paper.PathItem[] => {
        // Use reduce to accumulate merged cutouts and the evolving current cutout
        const { mergedCutouts, finalCutout } = cutouts.reduce(
            (acc, existingCutout) => {
                if (existingCutout instanceof this.paper.Path || existingCutout instanceof this.paper.CompoundPath) {
                    // Check if cutouts intersect or touch
                    if (
                        acc.finalCutout.intersects(existingCutout as paper.Item) ||
                        this.areCutoutsTouching(acc.finalCutout, existingCutout as paper.PathItem)
                    ) {
                        // Unite the cutouts to merge them
                        const united = acc.finalCutout.unite(existingCutout as paper.PathItem)
                        // Copy styling from the current cutout

                        united.strokeColor = acc.finalCutout.strokeColor
                        united.fillColor = acc.finalCutout.fillColor
                        united.opacity = acc.finalCutout.opacity
                        united.strokeWidth = acc.finalCutout.strokeWidth
                        united.data = acc.finalCutout.data

                        // Remove the old paths
                        acc.finalCutout.remove()
                        existingCutout.remove()

                        // Return updated accumulator with the merged cutout
                        return {
                            mergedCutouts: acc.mergedCutouts,
                            finalCutout: united,
                        }
                    }
                    // No intersection, keep the existing cutout

                    return {
                        mergedCutouts: [...acc.mergedCutouts, existingCutout as paper.PathItem],
                        finalCutout: acc.finalCutout,
                    }
                }

                return acc
            },
            { mergedCutouts: [] as paper.PathItem[], finalCutout: newCutout as paper.PathItem }
        )

        // Return all cutouts including the final merged one

        return [...mergedCutouts, finalCutout]
    }

    /**
     * Check if two cutouts are touching (sharing edges) by checking if their bounds touch
     * This is a fast approximation that works well for rectangular cutouts
     * @param cutout1 First cutout
     * @param cutout2 Second cutout
     * @returns true if cutouts are touching
     */
    private areCutoutsTouching = (cutout1: paper.PathItem, cutout2: paper.PathItem): boolean => {
        // Check if bounds are touching - this is fast and sufficient for most cases
        const bounds1 = cutout1.bounds
        const bounds2 = cutout2.bounds

        return this.areBoundsTouching(bounds1, bounds2)
    }

    /**
     * Check if two rectangles are touching or overlapping
     * Only merge if they actually overlap (intersect), not just touch edges
     * @param bounds1 First rectangle bounds
     * @param bounds2 Second rectangle bounds
     * @returns true if rectangles are overlapping (not just touching edges)
     */
    private areBoundsTouching = (bounds1: paper.Rectangle, bounds2: paper.Rectangle): boolean => {
        // Only return true if rectangles actually overlap (intersect)
        // This prevents merging when cutouts only touch along edges

        return bounds1.intersects(bounds2)
    }

    /**
     * Get the bounds of the parent opening. For compound paths, use the first child (main shape).
     * @param parent The parent item (could be Path or CompoundPath)
     * @returns The bounds of the main opening shape
     */
    private getParentBounds = (parent: paper.Item): paper.Rectangle => {
        if (parent instanceof this.paper.CompoundPath && parent.children.length > 0) {
            // First child is the main opening shape
            return parent.children[0].bounds
        }

        return parent.bounds
    }

    /**
     * Extracts the actual drawing path from a parent item, handling CompoundPath cases.
     * For CompoundPaths, returns the first child path (which typically contains the actual geometry).
     * For regular Paths, returns the path itself.
     * Returns null for unsupported item types or empty CompoundPaths.
     *
     * @param parent - The paper.Item to extract the path from (CompoundPath, Path, or other)
     * @returns The usable paper.Path or null if no valid path can be extracted
     */
    private getParentPath(parent: paper.Item): paper.Path | null {
        if (parent instanceof this.paper.CompoundPath && parent.children.length > 0) {
            return parent.children[0] as paper.Path
        } else if (parent instanceof this.paper.Path) {
            return parent
        }

        return null
    }

    /**
     * Check if the parent opening is large enough to accommodate a cutout
     * @param parentBounds The bounds of the parent opening
     * @returns true if the opening is large enough for cutouts
     */
    private isOpeningLargeEnoughForCutouts = (parentBounds: paper.Rectangle): boolean => {
        // Allow cutouts of any size - just need to be within the parent
        return parentBounds.width > 0 && parentBounds.height > 0
    }

    /**
     * Constructs the cutout item based on the type of the parent.
     *  parent === compound path -> append the cutout path as a child, merging with existing cutouts if they touch
     *  parent === path          -> create a new compound path with the original parent as the first child, cutouts are following children
     * @param parent
     * @param cutoutPath
     * @returns
     */
    private constructCutoutItem = (parent: paper.Item, cutoutPath: paper.Path): paper.CompoundPath => {
        // The new compound path will have a different ids so these needs to be updated
        this.setState('2D', {
            activeOpeningLocationId: parent.data.opening_location_id,
            activeDrawableId: parent.data.drawable_id,
            openingGroupId: parent.data.opening_group_id,
        })

        if (parent instanceof this.paper.CompoundPath) {
            // Get existing cutouts (all children except the first one which is the parent shape)
            const existingCutouts = parent.children.slice(1)
            const parentShape = parent.children[0]

            // Merge the new cutout with existing cutouts
            const mergedCutouts = this.mergeCutouts(existingCutouts, cutoutPath)

            // Remove all children and rebuild the compound path
            parent.removeChildren()
            parent.addChild(parentShape)
            mergedCutouts.forEach((cutout) => parent.addChild(cutout))

            return parent
        }
        const path = new this.paper.CompoundPath({
            children: [parent, cutoutPath],
        })

        this.setState('2D', {
            selectedItem: path.id,
        })

        return path
    }

    /**
     * Check if a rectangular cutout (defined by two corner points) is completely within the parent shape
     * @param from Starting corner of the rectangle
     * @param to Ending corner of the rectangle
     * @param parent The parent item
     * @returns true if the entire rectangle is within the parent shape
     */
    private isRectangleWithinParent = (from: paper.Point, to: paper.Point, parent: paper.Item): boolean => {
        const parentPath = this.getParentPath(parent)

        if (!parentPath) {
            return false
        }

        // Create a temporary rectangle to check
        const rect = new this.paper.Path.Rectangle(from, to)

        // Check all four corners of the rectangle
        const corners = [rect.bounds.topLeft, rect.bounds.topRight, rect.bounds.bottomLeft, rect.bounds.bottomRight]

        // Also check midpoints of each edge for better coverage
        const edgeMidpoints = [
            new this.paper.Point(rect.bounds.center.x, rect.bounds.top),
            new this.paper.Point(rect.bounds.center.x, rect.bounds.bottom),
            new this.paper.Point(rect.bounds.left, rect.bounds.center.y),
            new this.paper.Point(rect.bounds.right, rect.bounds.center.y),
        ]

        const pointsToCheck = [...corners, ...edgeMidpoints]

        // Check if all points are within the parent (no margin restriction)
        const allPointsValid = pointsToCheck.every((point) => {
            return parentPath.contains(point)
        })

        rect.remove()

        return allPointsValid
    }

    /**
     * Finds the area to cut out of. The selected item wins when the cursor is inside it;
     * otherwise any visible drawable area of the active group under the cursor can be used.
     */
    private resolveParentItem = (point: paper.Point): paper.Item | null => {
        const foundItem = this.cutoutItemId ? this.paper.project.getItem({ id: this.cutoutItemId }) : null

        // after the first cutout the selected path becomes the first child of a new compound path, while the stored
        // id still points to that child; cutting out of the child would pull it out of the compound path
        const selectedItem = foundItem?.parent instanceof this.paper.CompoundPath ? foundItem.parent : foundItem

        if (selectedItem?.contains(point)) return selectedItem

        if (!this.activeGroupId) return selectedItem ?? null

        const [area] = this.paper.project.getItems({
            match: (item: paper.Item) =>
                item.visible &&
                item.data.type === PAPER_ELEMENT_TYPE.DRAWABLE &&
                item.data.selectable !== false &&
                item.data.opening_group_id === this.activeGroupId &&
                item.data.shapeType === DRAWING_TYPES.AREA &&
                item.contains(point),
        })

        return area ?? selectedItem ?? null
    }

    /**
     * Handles the creation of the cutout rectangle.
     *  1st click -> create the cutout anchor point
     *  2nd click -> create the compound path & apply changes
     *  constraint -> cutout must be within the bound of the parent shape
     */
    onMouseDown = (event: paper.ToolEvent): void => {
        if (this.isPanningClick(event)) return

        let parent =
            this.startingPoint && this.cachedParentItem ? this.cachedParentItem : this.resolveParentItem(event.point)

        if (!parent) return

        // When multiple cutouts is drawn on the same iteration with area, Compound path has to be reassigned
        if (parent?.parent && parent.parent instanceof this.paper.CompoundPath) {
            parent = parent.parent
        }

        // Cache the parent item for use in onMouseMove
        this.cachedParentItem = parent

        // Ensure cutouts are completely within the parent opening bounds
        if (this.startingPoint && this.cutoutPath) {
            // Check if the ENTIRE rectangular cutout is completely within the parent shape
            const cutoutIsCompletelyWithinParent = this.isRectangleWithinParent(this.startingPoint, event.point, parent)

            if (!cutoutIsCompletelyWithinParent) {
                // If any part of the cutout extends outside the parent, cancel and allow user to try again
                this.cancel()

                return
            }

            this.startingPoint = null
            const parentDataCopy = { ...parent.data }
            const itemToUpdate = this.constructCutoutItem(parent, this.cutoutPath)

            // reset the rectangle id & cutout path so that it isn't removed from the compound path
            this.rectangleId = null
            this.cutoutPath = null

            this.styleNewCompoundPathShape(itemToUpdate, parent)

            // move the data to the new item
            itemToUpdate.data = parentDataCopy

            this.currentPaperId = null
            const dataToUpdate = this.convertPaperItemToAPICoordinateModel(itemToUpdate)
            const updatedCoordinates = dataToUpdate.coordinates
            const updatedCutouts = dataToUpdate.cutouts

            if (this.itemMetaDataUpdateFunction) {
                this.itemMetaDataUpdateFunction(itemToUpdate)

                if (itemToUpdate.data.onCutout) {
                    this.mediator.mediate('2D', {
                        latestAnalyticsAction: {
                            startOrStop: AnalyticsActionEdgeType.STOP,
                            actionType: AnalyticsActionType.EDIT,
                            coordinates: [[event.point.x, event.point.y]],
                        },
                    })

                    itemToUpdate.data.onCutout({
                        coordinates: updatedCoordinates,
                        measurements: dataToUpdate.measurements,
                        cutouts: updatedCutouts,
                        region_id: dataToUpdate.region_id,
                    })
                }
            }

            // Clear the cache after finishing the cutout
            this.cachedParentItem = null
        } else {
            // Check if the opening is large enough for cutouts with margin
            const parentBounds = this.getParentBounds(parent)

            if (!this.isOpeningLargeEnoughForCutouts(parentBounds)) {
                // Opening is too small for cutouts with safety margin
                return
            }

            let parentPath: paper.Path

            if (parent instanceof this.paper.CompoundPath && parent.children.length > 0) {
                parentPath = parent.children[0] as paper.Path
            } else if (parent instanceof this.paper.Path) {
                parentPath = parent
            } else {
                return
            }

            if (!parentPath.contains(event.point)) {
                // Click is outside the shape
                return
            }

            this.startingPoint = event.point
            this.mediator.mediate('2D', {
                latestAnalyticsAction: {
                    startOrStop: AnalyticsActionEdgeType.START,
                    actionType: AnalyticsActionType.EDIT,
                    coordinates: [[event.point.x, event.point.y]],
                },
            })
            this.draw(new this.paper.Color(this.color), this.startingPoint, this.startingPoint)
        }
    }

    onMouseUp = () => {
        this.setState('common', { cursor: this.cursor })
    }

    onMouseDrag = (event) => {
        this.toolPanning(event)
    }

    onMouseMove = (event: paper.MouseEvent): void => {
        if (this.startingPoint instanceof this.paper.Point && this.cachedParentItem) {
            // remove the previous rectangle that is no longer relevant
            if (this.rectangleId) this.paper.project.getItem({ id: this.rectangleId }).remove()

            const rect = this.draw(new this.paper.Color(this.color), this.startingPoint, event.point)

            const isValid = this.isRectangleWithinParent(this.startingPoint, event.point, this.cachedParentItem)

            if (!isValid) {
                rect.opacity = 0.3
            }

            this.rectangleId = rect.id
            this.cutoutPath = rect

            this.mediator.mediate('common', {
                tooltip: {
                    title: `Area: ${applyScaleFactorToPathArea({
                        pxValue: rect.area,
                        scaleFactor: this.scaleFactor,
                        dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                        xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                        yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                        pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                    }).toFixed(2)} sqft${!isValid ? ' (Invalid - extends outside shape)' : ''}`,
                    visible: true,
                    color: isValid ? '#000000' : '#ff0000',
                },
            })
        }
    }

    public setItemMetadataUpdateFunction(itemMetaDataUpdateFunction: (item: paper.Item) => void) {
        this.itemMetaDataUpdateFunction = itemMetaDataUpdateFunction
    }
}

export default Cutout
